import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Sparkles, Star, MessageCircle, Plus, Play, Pencil, Ruler, Weight } from "lucide-react";
import { Page } from "@/components/Page";
import { usePresence } from "@/lib/usePresence";
import { usePresenceToasts } from "@/lib/usePresenceToasts";
import { api, resolveImg } from "@/lib/api";
import { embedUrl } from "@/lib/media";
import { useCart } from "@/context/CartContext";
import { useSettings, buildWaLink } from "@/context/SettingsContext";
import { useSeo } from "@/lib/useSeo";

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [active, setActive] = useState(0);
  const [variant, setVariant] = useState("");
  const { add } = useCart();
  const { settings } = useSettings();
  usePresenceToasts(usePresence(id));
  useSeo(product ? { title: `${product.name} — 3Duo`, description: product.description, image: resolveImg(product.images?.[0]) } : null);

  useEffect(() => {
    api.get(`/products/${id}`)
      .then((r) => { setProduct(r.data); setActive(0); setVariant(""); })
      .catch(() => setProduct(false));
  }, [id]);

  // Images first, then videos - uploaded files play inline, YouTube/Vimeo
  // links are embedded.
  const media = useMemo(() => {
    if (!product) return [];
    const images = (product.images || []).map((src) => ({ kind: "image", src }));
    const videos = (product.videos || []).map((src) => {
      const embed = embedUrl(src);
      return embed ? { kind: "embed", src, embed } : { kind: "video", src };
    });
    return [...images, ...videos];
  }, [product]);

  if (product === null) return <Page className="pt-40 text-center min-h-screen">A carregar...</Page>;
  if (product === false) return <Page className="pt-40 text-center min-h-screen">Produto não encontrado. <Link to="/produtos" className="text-primary underline">Voltar</Link></Page>;

  const current = media[active];
  const variants = product.variants || [];

  // A chosen model's own measurements win; anything it leaves blank falls back
  // to the product's, so a shop only has to fill in what actually differs.
  const chosen = variants.find((v) => v.label === variant);
  const specs = {
    dimensions: chosen?.dimensions || product.dimensions || "",
    weight: chosen?.weight || product.weight || "",
  };

  // Picking a model also brings its photograph into the main viewer, so the
  // choice is visible rather than just highlighted in a grid.
  const jumpToVariantImage = (v) => {
    const index = media.findIndex((m) => m.kind === "image" && m.src === v.image);
    if (index >= 0) setActive(index);
  };

  return (
    <Page className="pt-32 md:pt-40 px-6 md:px-12 max-w-6xl mx-auto min-h-screen">
      <Link to="/produtos" data-testid="back-to-products" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8 font-medium">
        <ArrowLeft size={18} /> Voltar aos produtos
      </Link>

      <div className="grid lg:grid-cols-2 gap-10">
        <div>
          <motion.div key={active} initial={{ opacity: 0.4 }} animate={{ opacity: 1 }}
            data-testid="product-media"
            className="relative aspect-square rounded-[2rem] overflow-hidden bg-muted border border-border grid place-items-center">
            {!current && null}
            {current?.kind === "image" && (
              <>
                {/*
                  A piece photographed upright is taller than the square frame.
                  Cropping it to fill showed only a slice, so the image is
                  contained in full and a blurred copy fills the sides.
                */}
                <img src={resolveImg(current.src)} alt="" aria-hidden="true"
                  className="absolute inset-0 w-full h-full object-cover scale-125 blur-2xl opacity-50" />
                {/*
                  Absolute, not w-full h-full: the frame is a centred grid, so
                  a grid item's height stays auto and h-full resolves to
                  nothing. A tall photograph then renders at its own height and
                  overflow-hidden clips it - object-contain and all.
                */}
                <img src={resolveImg(current.src)} alt={product.name}
                  className="absolute inset-0 w-full h-full object-contain" />
              </>
            )}
            {current?.kind === "video" && (
              <video
                src={resolveImg(current.src)}
                controls
                playsInline
                preload="metadata"
                data-testid="product-video"
                className="absolute inset-0 w-full h-full object-contain bg-black"
              />
            )}
            {current?.kind === "embed" && (
              <iframe
                src={current.embed}
                title={`${product.name} — vídeo`}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                data-testid="product-embed"
                className="absolute inset-0 w-full h-full border-0"
              />
            )}
          </motion.div>

          {media.length > 1 && (
            <div className="flex gap-3 mt-4 flex-wrap" data-testid="gallery-thumbs">
              {media.map((m, i) => (
                <button key={i} onClick={() => setActive(i)}
                  aria-label={m.kind === "image" ? `Imagem ${i + 1}` : `Vídeo ${i + 1}`}
                  className={`w-20 h-20 rounded-2xl overflow-hidden border-2 transition-colors grid place-items-center bg-muted ${active === i ? "border-primary" : "border-transparent"}`}>
                  {m.kind === "image"
                    ? <img src={resolveImg(m.src)} alt="" className="w-full h-full object-cover" />
                    : <Play size={22} className="text-muted-foreground" />}
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <div className="flex flex-wrap gap-2 mb-4">
            {product.is_new && <span className="inline-flex items-center gap-1 rounded-full bg-secondary text-foreground text-xs font-bold px-3 py-1"><Sparkles size={12}/> Novo</span>}
            {product.is_featured && <span className="inline-flex items-center gap-1 rounded-full brand-gradient text-white text-xs font-bold px-3 py-1"><Star size={12}/> Destaque</span>}
            {product.is_customizable && <span className="inline-flex items-center gap-1 rounded-full bg-foreground text-white text-xs font-bold px-3 py-1"><Pencil size={12}/> Personalizável</span>}
          </div>
          <span className="text-sm font-semibold text-primary uppercase tracking-wide">{product.category}</span>
          <h1 className="font-display font-black text-3xl md:text-5xl tracking-tight mt-2">{product.name}</h1>
          {product.price && <p className="font-display font-extrabold text-2xl mt-4">{product.price}</p>}
          <p className="text-foreground/70 leading-relaxed mt-6 text-lg">{product.description}</p>

          {variants.length > 0 && (
            <div className="mt-7" data-testid="variant-picker">
              <p className="text-sm font-semibold mb-3">
                Escolhe o modelo{variant ? "" : " *"}
              </p>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                {variants.map((v) => (
                  <button
                    key={v.label}
                    onClick={() => { setVariant(v.label); jumpToVariantImage(v); }}
                    data-testid={`detail-variant-${v.label}`}
                    className={`rounded-2xl border-2 overflow-hidden text-left transition-colors ${
                      variant === v.label ? "border-primary" : "border-border hover:border-muted-foreground/40"
                    }`}
                  >
                    <div className="aspect-square bg-muted">
                      {/* Contained, not cropped: choosing a model means being
                          able to tell one from another. */}
                      {v.image && <img src={resolveImg(v.image)} alt={v.label} className="w-full h-full object-contain" />}
                    </div>
                    <p className="text-xs font-semibold p-2 leading-tight">{v.label}</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {(specs.dimensions || specs.weight) && (
            <dl data-testid="product-specs" className="mt-6 rounded-2xl border border-border bg-card divide-y divide-border text-sm">
              {specs.dimensions && (
                <div className="flex items-center gap-3 px-5 py-3">
                  <Ruler size={16} className="text-muted-foreground shrink-0" />
                  <dt className="text-muted-foreground">Tamanho</dt>
                  <dd data-testid="spec-dimensions" className="ml-auto font-semibold">{specs.dimensions}</dd>
                </div>
              )}
              {specs.weight && (
                <div className="flex items-center gap-3 px-5 py-3">
                  <Weight size={16} className="text-muted-foreground shrink-0" />
                  <dt className="text-muted-foreground">Peso</dt>
                  <dd data-testid="spec-weight" className="ml-auto font-semibold">{specs.weight}</dd>
                </div>
              )}
            </dl>
          )}

          {product.is_customizable && (
            <p data-testid="customization-hint" className="mt-5 rounded-2xl border border-border bg-card px-5 py-4 text-sm">
              <span className="font-semibold">Este produto é personalizado.</span>{" "}
              Ao adicionar ao orçamento vamos pedir-te: {product.customization_label}
            </p>
          )}

          <div className="flex flex-wrap gap-3 mt-8">
            <button
              onClick={() => add(product, variant ? { variant } : {})}
              data-testid="detail-add-to-cart"
              className="inline-flex items-center gap-2 rounded-full brand-gradient text-white font-bold px-8 py-4 transition-transform hover:scale-105 active:scale-95 shadow-lg"
            >
              <Plus size={20} /> {product.is_customizable ? "Personalizar e adicionar" : "Adicionar ao orçamento"}
            </button>
            <a
              href={buildWaLink(settings?.contacts, `Olá 3Duo! Tenho interesse no produto "${product.name}". Podem dar-me mais informações?`)}
              target="_blank" rel="noreferrer"
              data-testid="product-whatsapp-btn"
              className="inline-flex items-center gap-2 rounded-full bg-[#25D366] text-white font-bold px-8 py-4 transition-transform hover:scale-105 active:scale-95 shadow-lg"
            >
              <MessageCircle size={20} /> Falar no WhatsApp
            </a>
          </div>
        </div>
      </div>
    </Page>
  );
}
